// Certificates tab — "My Certificates" on top, then the full Badges section below.
// Mirrors the Loyalty App where certifications & badges live under one tab.
import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useCourseLang } from '@/lib/courseLang';
import { motion } from 'framer-motion';
import { GraduationCap, BadgeCheck, CalendarDays } from 'lucide-react';

const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return ''; }
};

export default function TraineeCertificates() {
    const [certs, setCerts] = useState(null);
    const [lang] = useCourseLang();

    useEffect(() => {
        setCerts(null);
        api.get(`/trainee/certificates?language=${encodeURIComponent(lang || 'en')}`)
            .then((r) => setCerts(r.data || []))
            .catch(() => setCerts([]));
    }, [lang]);

    return (
        <div className="max-w-2xl mx-auto px-4 py-5" data-testid="trainee-certificates-page">
            {/* My Certifications only (badges live on the Home screen) */}
            <section>
                <div className="rounded-3xl bg-brand-navy text-white p-6 relative overflow-hidden mb-4">
                    <div className="absolute -right-6 -top-6 opacity-10"><GraduationCap className="w-32 h-32" /></div>
                    <div className="text-brand-yellow/80 text-xs font-bold uppercase tracking-wider">My Certifications</div>
                    <div className="flex items-end gap-2 mt-1">
                        <span className="text-4xl font-black text-brand-yellow" data-testid="cert-count">{certs ? certs.length : '—'}</span>
                        <span className="text-white/70 text-sm mb-1">certificate{certs && certs.length === 1 ? '' : 's'} earned</span>
                    </div>
                </div>

                {certs === null ? (
                    <div className="text-center text-white/60 py-8">Loading certificates…</div>
                ) : certs.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-brand-navy/5 p-8 text-center text-brand-navy/50" data-testid="cert-empty">
                        <GraduationCap className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        Pass a course&apos;s final quiz to earn your first certificate.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {certs.map((c, i) => (
                            <motion.div
                                key={c.certificate_id || c.id}
                                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                                className="bg-white rounded-2xl border border-brand-navy/5 p-4 flex items-center gap-4 shadow-sm"
                                data-testid={`cert-card-${c.certificate_id}`}>
                                <div className="h-14 w-14 rounded-2xl grid place-items-center bg-gradient-to-br from-emerald-500 to-teal-600 text-white shrink-0">
                                    <BadgeCheck className="w-7 h-7" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-brand-black text-sm leading-tight truncate">{c.course_title}</div>
                                    <div className="text-[13px] text-brand-navy/50 mt-0.5">ID: {c.certificate_id}</div>
                                    <div className="text-[13px] text-brand-navy/55 mt-1 flex items-center gap-1">
                                        <CalendarDays className="w-3 h-3" /> {fmtDate(c.issued_at)}
                                    </div>
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="text-emerald-600 font-black text-lg leading-none">{Math.round(c.score)}%</div>
                                    <div className="text-[12px] text-brand-navy/40 uppercase">score</div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
